import { useState, useEffect, useCallback, useMemo } from 'react';
import { Lead } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import {
  subscribeToLeads,
  addLead as addLeadToFirebase,
  updateLead as updateLeadInFirebase,
  checkLeadExists as checkLeadExistsInFirebase,
} from '@/services/leadService';
import { getAllActiveUsers } from '@/services/userService';
import { populateUserCache } from '@/data/mockData';

/** Nobody gets broad query — all users use createdBy + sharedWith queries */
const checkIsBH = (_roles: string[] | undefined): boolean => {
  return false;
};

export function useLeads() {
  const { user } = useAuth();
  const [allLeads, setAllLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);

  const isBH = useMemo(() => checkIsBH(user?.roles), [user?.roles]);

  // Populate user name cache once so LeadCard can display real names
  useEffect(() => {
    getAllActiveUsers()
      .then(users => populateUserCache(users))
      .catch(() => {/* non-critical */});
  }, []);

  // Subscribe to leads with proper Firestore-rule-matching queries
  useEffect(() => {
    if (!user) return;

    setLoading(true);

    const unsubscribe = subscribeToLeads(
      (updatedLeads) => {
        setAllLeads(updatedLeads);
        setLoading(false);
      },
      {
        userId: user.id,
        officeId: user.officeId,
        isBH,
      },
    );

    return () => {
      unsubscribe();
    };
  }, [user, isBH]);

  // Client-side filter: keep leads the user should see
  // For BH/admin the query already returns everything, so just pass through.
  // For regular users the two Firestore queries already scope results, but we
  // still apply a lightweight filter to include assignedTo matches that might
  // come through the office query.
  const leads = useMemo(() => {
    if (!user) return allLeads;
    // Ops/BH sees everything that Firestore returned
    if (isBH) return allLeads;

    return allLeads.filter(lead => {
      // Show if user created the lead
      if (lead.createdBy === user.id) return true;
      // Show if user is assigned to the lead
      if (lead.assignedTo === user.id) return true;
      // Show if lead is shared with user
      if (lead.sharedWith?.includes(user.id)) return true;
      return false;
    });
  }, [allLeads, user, isBH]);

  const addLead = useCallback(async (lead: Omit<Lead, 'id'>): Promise<string> => {
    try {
      return await addLeadToFirebase(lead);
    } catch (error) {
      console.error('Error adding lead:', error);
      throw error;
    }
  }, []);

  const updateLead = useCallback(async (leadId: string, updates: Partial<Lead>) => {
    try {
      await updateLeadInFirebase(leadId, updates);
    } catch (error) {
      console.error('Error updating lead:', error);
      throw error;
    }
  }, []);

  const getLead = useCallback((id: string) => {
    return allLeads.find(l => l.id === id);
  }, [allLeads]);

  const checkLeadExists = useCallback(async (phone: string, excludeId?: string): Promise<boolean> => {
    return await checkLeadExistsInFirebase(phone, excludeId, user?.officeId);
  }, [user?.officeId]);

  return { leads, addLead, updateLead, getLead, checkLeadExists, loading };
}
